import React, { useCallback, useEffect, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import './LocationManagers.css';

interface Location {
  id: number;
  name: string;
  city: string;
}

interface Recipient {
  id: number;
  email: string;
  name: string | null;
  is_active: boolean;
  added_by: string | null;
  added_at: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

interface LocationEmailRecipientsProps {
  location: Location;
}

/**
 * Losse e-mailadressen die bericht krijgen bij een reservering zonder beheerder
 * te zijn. Zij kunnen niet inloggen en hun melding bevat geen gegevens van de
 * reserveerder, dus de beheerder van de locatie regelt dit zelf.
 */
const LocationEmailRecipients: React.FC<LocationEmailRecipientsProps> = ({ location }) => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const [confirmRemove, setConfirmRemove] = useState<Recipient | null>(null);

  const fetchRecipients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ApiResponse<Recipient[]>>(
        `/locations/${location.id}/email-recipients`
      );
      setRecipients(response.data.data || []);
    } catch (err) {
      setError(
        permissionMessage(err) || 'De extra e-mailadressen van deze locatie konden niet geladen worden.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [location.id]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  const apiError = (err: unknown) =>
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

  const markSaving = (id: number, saving: boolean) =>
    setSavingIds((current) => (saving ? [...current, id] : current.filter((item) => item !== id)));

  const activeCount = recipients.filter((recipient) => recipient.is_active).length;

  const addRecipient = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newEmail.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post<ApiResponse<Recipient>>(
        `/locations/${location.id}/email-recipients`,
        { email: newEmail.trim(), name: newName.trim() || undefined }
      );
      setNewEmail('');
      setNewName('');
      setSuccess(response.data.message || 'E-mailadres toegevoegd.');
      await fetchRecipients();
    } catch (err) {
      setError(permissionMessage(err) || apiError(err) || 'Het e-mailadres kon niet toegevoegd worden.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (recipient: Recipient) => {
    markSaving(recipient.id, true);
    setError(null);
    setSuccess(null);

    const next = !recipient.is_active;
    try {
      const response = await apiClient.put<ApiResponse<Recipient>>(
        `/locations/${location.id}/email-recipients/${recipient.id}`,
        { is_active: next }
      );
      setRecipients((current) =>
        current.map((item) => (item.id === recipient.id ? { ...item, is_active: next } : item))
      );
      setSuccess(response.data.message || 'Instelling opgeslagen.');
    } catch (err) {
      setError(permissionMessage(err) || apiError(err) || 'De instelling kon niet opgeslagen worden.');
    } finally {
      markSaving(recipient.id, false);
    }
  };

  const removeRecipient = async () => {
    if (!confirmRemove) return;

    markSaving(confirmRemove.id, true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.delete<ApiResponse<unknown>>(
        `/locations/${location.id}/email-recipients/${confirmRemove.id}`
      );
      setSuccess(response.data.message || 'E-mailadres verwijderd.');
      setConfirmRemove(null);
      await fetchRecipients();
    } catch (err) {
      setError(permissionMessage(err) || apiError(err) || 'Het e-mailadres kon niet verwijderd worden.');
    } finally {
      markSaving(confirmRemove.id, false);
    }
  };

  return (
    <div className="location-managers">
      <div className="managers-intro">
        <h2>Extra e-mailadressen van {location.name}</h2>
        <p>
          Deze mensen krijgen bericht als er een fiets van deze locatie gereserveerd of geannuleerd
          wordt, zonder dat zij beheerder zijn. Zij kunnen niet inloggen en zien in de e-mail geen
          naam, e-mailadres, telefoonnummer of notities van de reserveerder: alleen welke fiets,
          wanneer en het reserveringsnummer.
        </p>
      </div>

      {error && <div className="managers-message managers-message-error">{error}</div>}
      {success && <div className="managers-message managers-message-success">{success}</div>}

      <form className="manager-form" onSubmit={addRecipient}>
        <div className="manager-form-fields">
          <div className="manager-form-group">
            <label htmlFor="recipient-email">E-mailadres</label>
            <input
              id="recipient-email"
              type="email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              placeholder="naam@voorbeeld.nl"
              required
              disabled={isSaving}
            />
          </div>
          <div className="manager-form-group">
            <label htmlFor="recipient-name">Omschrijving (optioneel)</label>
            <input
              id="recipient-name"
              type="text"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Bijvoorbeeld: coordinator vrijwilligers"
              disabled={isSaving}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={isSaving || !newEmail.trim()}>
            {isSaving ? 'Bezig...' : 'Adres toevoegen'}
          </button>
        </div>
        <p className="manager-form-hint">
          Er gaat alleen een bericht uit voor fietsen waarbij "E-mail naar beheerders" aan staat. Dat
          zet u per fiets aan bij Fietsen.
        </p>
      </form>

      <div className="managers-toolbar">
        <div className="managers-count">
          {recipients.length} adres{recipients.length === 1 ? '' : 'sen'} · {activeCount} actief
        </div>
      </div>

      {isLoading ? (
        <p className="managers-loading">E-mailadressen laden...</p>
      ) : recipients.length === 0 ? (
        <div className="managers-empty">
          Er zijn nog geen extra e-mailadressen voor deze locatie.
        </div>
      ) : (
        <ul className="managers-list">
          {recipients.map((recipient) => {
            const isBusy = savingIds.includes(recipient.id);

            return (
              <li key={recipient.id} className="manager-row">
                <div className="manager-identity">
                  <span className="manager-name">{recipient.name || recipient.email}</span>
                  {recipient.name && <span className="manager-email">{recipient.email}</span>}
                  {recipient.added_by && (
                    <span className="manager-email">Toegevoegd door {recipient.added_by}</span>
                  )}
                </div>

                <div className="manager-controls">
                  <label className="manager-toggle">
                    <input
                      type="checkbox"
                      checked={recipient.is_active}
                      onChange={() => toggleActive(recipient)}
                      disabled={isBusy}
                    />
                    <span className="manager-toggle-track" aria-hidden="true">
                      <span className="manager-toggle-thumb" />
                    </span>
                    <span className="manager-toggle-label">
                      {recipient.is_active ? 'Ontvangt e-mail' : 'Geen e-mail'}
                    </span>
                  </label>

                  <button
                    type="button"
                    className="manager-remove"
                    onClick={() => setConfirmRemove(recipient)}
                    disabled={isBusy}
                    title={`${recipient.email} verwijderen`}
                    aria-label={`${recipient.email} verwijderen`}
                  >
                    ✕
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmRemove && (
        <div className="modal-overlay">
          <div className="modal-content managers-confirm">
            <h3>E-mailadres verwijderen</h3>
            <p>
              Weet je zeker dat je <strong>{confirmRemove.email}</strong> wilt verwijderen bij{' '}
              {location.name}?
            </p>
            <p className="managers-confirm-hint">
              Dit adres krijgt daarna geen berichten meer. Wil je het later weer gebruiken, zet het
              dan liever tijdelijk uit met het schuifje.
            </p>
            <div className="managers-confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmRemove(null)}
              >
                Annuleren
              </button>
              <button type="button" className="btn btn-danger" onClick={removeRecipient}>
                Ja, verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationEmailRecipients;
